export interface MMURegion {
  name: string;
  read8: (address: number) => number;
  write8: (address: number, value: number) => void;
}

export interface MMUSnapshot {
  ramRegions: Array<{ segment: number; data: Uint8Array }>;
}

export class MMU {
  private segments: (MMURegion | null)[] = new Array(256).fill(null);
  private ramBuffers: Map<number, Uint8Array> = new Map();

  constructor() {}

  mapSegment(segment: number, region: MMURegion): void {
    this.segments[segment] = region;
  }

  unmapSegment(segment: number): void {
    this.segments[segment] = null;
  }

  getRegion(segment: number): MMURegion | null {
    return this.segments[segment];
  }

  read8(address: number): number {
    const segment = (address >>> 16) & 0xFF;
    const region = this.segments[segment];
    if (region === null) return 0xFF;
    return region.read8(address);
  }

  write8(address: number, value: number): void {
    const segment = (address >>> 16) & 0xFF;
    const region = this.segments[segment];
    if (region === null) return;
    region.write8(address, value);
  }

  read16(address: number): number {
    return this.read8(address) | (this.read8(address + 1) << 8);
  }

  write16(address: number, value: number): void {
    this.write8(address, value & 0xFF);
    this.write8(address + 1, (value >>> 8) & 0xFF);
  }

  read24(address: number): number {
    return this.read8(address) | (this.read8(address + 1) << 8) | (this.read8(address + 2) << 16);
  }

  write24(address: number, value: number): void {
    this.write8(address, value & 0xFF);
    this.write8(address + 1, (value >>> 8) & 0xFF);
    this.write8(address + 2, (value >>> 16) & 0xFF);
  }

  readCode(address: number): number {
    return this.read8(address);
  }

  readCode16(address: number): number {
    return this.read16(address);
  }

  getRAMBuffer(segment: number): Uint8Array | null {
    return this.ramBuffers.get(segment) ?? null;
  }

  mapRAM(segment: number, size: number = 0x10000): void {
    const buffer = new Uint8Array(size);
    this.ramBuffers.set(segment, buffer);
    this.mapSegment(segment, {
      name: 'RAM',
      read8: (address) => buffer[address & 0xFFFF],
      write8: (address, value) => { buffer[address & 0xFFFF] = value; },
    });
  }

  saveSnapshot(): MMUSnapshot {
    const ramRegions: Array<{ segment: number; data: Uint8Array }> = [];
    for (const [segment, buffer] of this.ramBuffers) {
      ramRegions.push({ segment, data: new Uint8Array(buffer) });
    }
    return { ramRegions };
  }

  loadSnapshot(snapshot: MMUSnapshot): void {
    for (const { segment, data } of snapshot.ramRegions) {
      const buffer = this.ramBuffers.get(segment);
      if (buffer) {
        buffer.set(data.subarray(0, buffer.length));
      }
    }
  }
}
